import React, { useCallback, useEffect, useState } from 'react';
import cx from 'classnames';
import { Tooltip, message } from 'antd';
import styles from './index.module.less';
import { useComposition } from './useComposition';
import { useUbcSend } from '@hooks/useUbcSend';
import { useImageUpload } from './useImageUpload';
import type { ImageItem } from './useImageUpload';
import ImagePreview from './ImagePreview';
import DragOverlay from './DragOverlay';

let iconSubmit = 'https://muzhi-public-pic.cdn.bcebos.com/100115321300';
let stopImage = 'https://muzhi-public-pic.cdn.bcebos.com/100115348163';

const uploadIcon = 'https://med-fe.cdn.bcebos.com/fe-doctor-workbench/upload.png';
const uploadIcon2 = 'https://med-fe.cdn.bcebos.com/fe-doctor-workbench/upload-2.png';
interface ISearchInputProps {
    placeholder?: string;
    onSubmit?: (value: string, images?: ImageItem[]) => Promise<void>;
    /** 停止 SSE 连接 */
    onStop?: () => void;
    className?: string;
    /** 是否禁用提交按钮 */
    sseLoading?: boolean;
    /** 输入框最小高度 */
    minHeight?: number;
    /** 输入框最大高度 */
    maxHeight?: number;
    paddingTop?: number;
}

/**
 * 搜索输入框组件
 * 支持富文本编辑、图片上传和提交功能
 */
const SearchInput: React.FC<ISearchInputProps> = ({
    placeholder = '请输入您的医学问题',
    onSubmit,
    onStop,
    className,
    sseLoading = false,
    minHeight = 100,
    maxHeight = 299,
}) => {
    const inputRef = React.useRef<HTMLDivElement>(null);
    const [hasContent, setHasContent] = React.useState(false);
    const isSubmittingRef = React.useRef(false);
    const { isComposingRef, handleCompositionStart, handleCompositionEnd } = useComposition();
    const MAX_LENGTH = 2000;
    const { ubcSend } = useUbcSend();
    const [isDragging, setIsDragging] = useState(false);
    const dragCounterRef = React.useRef(0);

    const {
        imageList,
        isMaxCount,
        isAllUploaded,
        isUploading,
        fileInputRef,
        removeImage,
        clearImages,
        retryImage,
        triggerFileSelect,
        handleFileInputChange,
        handlePasteImages,
        handleDropFiles,
    } = useImageUpload();

    const hasImages = imageList.length > 0;
    const canSubmit = (hasContent || (hasImages && isAllUploaded)) && !isUploading;

    /**
     * 检查并限制字符数
     */
    const checkAndLimitLength = useCallback(() => {
        if (!inputRef.current) return;
        const text = inputRef.current.innerText || '';
        if (text.length > MAX_LENGTH) {
            inputRef.current.innerText = text.substring(0, MAX_LENGTH);
            const selection = window.getSelection();
            if (selection) {
                const range = document.createRange();
                range.selectNodeContents(inputRef.current);
                range.collapse(false);
                selection.removeAllRanges();
                selection.addRange(range);
            }
        }
    }, []);

    const focusInput = useCallback(() => {
        if (inputRef.current) {
            inputRef.current.focus();
            const selection = window.getSelection();
            if (!selection) return;
            const range = document.createRange();
            range.selectNodeContents(inputRef.current);
            range.collapse(false);
            selection.removeAllRanges();
            selection.addRange(range);
        }
    }, []);

    useEffect(() => {
        focusInput();
    }, [focusInput]);

    useEffect(() => {
        isSubmittingRef.current = false;
    }, [sseLoading]);

    /**
     * 处理提交
     */
    const handleSubmit = useCallback(() => {
        if (isSubmittingRef.current) return;
        if (inputRef.current) {
            const value = inputRef.current.innerText.trim();
            const images = imageList.length > 0 ? imageList : undefined;

            // 有图片但还在上传中，不允许提交
            if (images && isUploading) {
                message.warning('图片正在上传中，请稍候');
                return;
            }

            // 有图片但有上传失败的，过滤掉失败项
            const validImages = images?.filter((img) => img.uploadStatus === 'success');

            if ((value || (validImages && validImages.length > 0)) && onSubmit) {
                isSubmittingRef.current = true;
                onSubmit(value, validImages && validImages.length > 0 ? validImages : undefined)
                    .then(() => {
                        if (!inputRef.current) return;
                        inputRef.current.innerText = '';
                        setHasContent(false);
                        clearImages();
                        ubcSend({
                            type: 'clk',
                            value: 'ask_msg',
                            ext: {
                                product_info: {
                                    ask_type: images ? 'imageInput' : 'manualInput',
                                    content: value,
                                    imgCount: images?.length || 0,
                                },
                            },
                        });
                        isSubmittingRef.current = false;
                    })
                    .catch(() => {
                        isSubmittingRef.current = false;
                    });
            }
        }
    }, [onSubmit, ubcSend, imageList, clearImages, isUploading]);

    const handleInput = useCallback(() => {
        if (!inputRef.current) return;
        checkAndLimitLength();
        const text = inputRef.current.innerText.trim();
        if (text === '' && inputRef.current.innerHTML !== '') {
            inputRef.current.textContent = '';
        }
        setHasContent(text.length > 0);
    }, [checkAndLimitLength]);

    /**
     * 处理粘贴事件（支持图片粘贴）
     */
    const handlePaste = useCallback(
        (e: React.ClipboardEvent<HTMLDivElement>) => {
            // SSE 回复中禁止上传图片
            if (sseLoading) return;

            // 先检查是否有图片
            if (handlePasteImages(e.clipboardData)) {
                e.preventDefault();
                return;
            }

            e.preventDefault();
            if (!inputRef.current) return;

            const pastedText = e.clipboardData.getData('text/plain');
            const selection = window.getSelection();
            if (!selection || selection.rangeCount === 0) return;

            const range = selection.getRangeAt(0);
            const selectedText = range.toString();
            const selectedLength = selectedText.length;
            const currentText = inputRef.current.innerText || '';
            const afterPasteLength = currentText.length - selectedLength + pastedText.length;

            let textToInsert = pastedText;
            if (afterPasteLength > MAX_LENGTH) {
                const allowedLength = MAX_LENGTH - (currentText.length - selectedLength);
                textToInsert = pastedText.substring(0, Math.max(0, allowedLength));
            }

            if (textToInsert.length === 0) return;

            range.deleteContents();
            const textNode = document.createTextNode(textToInsert);
            range.insertNode(textNode);
            range.setStartAfter(textNode);
            range.collapse(true);
            selection.removeAllRanges();
            selection.addRange(range);
            handleInput();
        },
        [handleInput, handlePasteImages, sseLoading]
    );

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLDivElement>) => {
            if (isComposingRef.current) return;
            if (sseLoading) return;
            if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSubmit();
            }
        },
        [handleSubmit, isComposingRef, sseLoading]
    );

    const handleWrapperClick = useCallback(
        (e: React.MouseEvent<HTMLDivElement>) => {
            if (e.target === inputRef.current) return;
            focusInput();
        },
        [focusInput]
    );

    // 拖拽事件处理 - 监听全局 document，拖到页面任意位置均可触发
    useEffect(() => {
        const handleDragEnter = (e: DragEvent) => {
            if (!e.dataTransfer?.types.includes('Files')) return;
            e.preventDefault();
            dragCounterRef.current++;
            if (dragCounterRef.current === 1) {
                setIsDragging(true);
            }
        };

        const handleDragLeave = (e: DragEvent) => {
            e.preventDefault();
            dragCounterRef.current--;
            if (dragCounterRef.current === 0) {
                setIsDragging(false);
            }
        };

        const handleDragOver = (e: DragEvent) => {
            if (!e.dataTransfer?.types.includes('Files')) return;
            e.preventDefault();
        };

        const handleDrop = (e: DragEvent) => {
            e.preventDefault();
            dragCounterRef.current = 0;
            setIsDragging(false);
            // SSE 回复中禁止上传图片
            if (sseLoading || !e.dataTransfer) return;
            handleDropFiles(e.dataTransfer);
        };

        document.addEventListener('dragenter', handleDragEnter);
        document.addEventListener('dragleave', handleDragLeave);
        document.addEventListener('dragover', handleDragOver);
        document.addEventListener('drop', handleDrop);

        return () => {
            document.removeEventListener('dragenter', handleDragEnter);
            document.removeEventListener('dragleave', handleDragLeave);
            document.removeEventListener('dragover', handleDragOver);
            document.removeEventListener('drop', handleDrop);
        };
    }, [handleDropFiles, sseLoading]);

    // 全局键盘事件（ESC关闭预览等）
    useEffect(() => {
        const handleGlobalKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                setIsDragging(false);
            }
        };
        document.addEventListener('keydown', handleGlobalKeyDown);
        return () => document.removeEventListener('keydown', handleGlobalKeyDown);
    }, []);

    return (
        <section className={cx(styles.searchSection, className, 'relative')}>
            <div className={cx(styles.searchBoxWrapper, 'searchBoxWrapper')} onClick={handleWrapperClick}>
                {/* 图片预览区域 */}
                <ImagePreview
                    imageList={imageList}
                    isMaxCount={isMaxCount}
                    onAdd={triggerFileSelect}
                    onRemove={removeImage}
                    onRetry={retryImage}
                />

                <div
                    className={cx(styles.searchBox)}
                    style={{
                        minHeight: minHeight ? minHeight - 78 + 'px' : '',
                        maxHeight: maxHeight ? maxHeight - 78 + 'px' : '',
                    }}
                >
                    <div
                        ref={inputRef}
                        className={cx(styles.editor, 'placeholder editor')}
                        contentEditable="true"
                        data-beforecontent={placeholder}
                        onCompositionStart={handleCompositionStart}
                        onCompositionEnd={handleCompositionEnd}
                        onKeyDown={handleKeyDown}
                        onInput={handleInput}
                        onPaste={handlePaste}
                    />
                </div>
            </div>

            {/* 底部操作区：图片icon + 发送按钮 */}
            <div className={styles.bottomActions}>
                {/* 图片上传按钮 */}
                <Tooltip
                    title="支持jpg、png、jpeg等，最多5张，最大10MB"
                    rootClassName={styles.tooltip}
                    arrow={false}
                    align={{ offset: [0, -14] }}
                >
                    <button
                        className={cx(styles.imageButton, { [styles.disabled]: isMaxCount || sseLoading })}
                        onClick={triggerFileSelect}
                        disabled={isMaxCount || sseLoading}
                        aria-label="上传图片"
                        onMouseEnter={(e) =>
                            ((e.currentTarget.querySelector('img') as HTMLImageElement).src = uploadIcon2)
                        }
                        onMouseLeave={(e) =>
                            ((e.currentTarget.querySelector('img') as HTMLImageElement).src = uploadIcon)
                        }
                    >
                        <img src={uploadIcon} width={20} height={20} alt="" />
                    </button>
                </Tooltip>

                {/* 发送按钮 */}
                <Tooltip title={sseLoading ? '停止生成' : ''} open={sseLoading ? undefined : false}>
                    <button
                        className={styles.submitButton}
                        onClick={sseLoading ? onStop : handleSubmit}
                        disabled={!sseLoading && !canSubmit}
                        aria-label={sseLoading ? '停止' : '提交搜索'}
                    >
                        <img
                            src={sseLoading ? stopImage : iconSubmit}
                            alt=""
                            className={cx(styles.submitIcon, { [styles.loading]: sseLoading })}
                        />
                    </button>
                </Tooltip>
            </div>

            {/* 隐藏的文件选择器 */}
            <input
                ref={fileInputRef}
                type="file"
                accept=".jpg,.jpeg,.png,.gif"
                multiple
                style={{ display: 'none' }}
                onChange={handleFileInputChange}
            />

            {/* 拖拽遮罩 */}
            {isDragging && <DragOverlay />}
        </section>
    );
};

export default SearchInput;
